import { useCallback, useMemo } from 'react';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { searchCoverageHours } from 'graphql-queries';
import { convertToUUID } from 'uuid-encoding';

export const COVERAGE_HOURS_PAGE_SIZE = 25;

export class CoverageHoursQueryError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'CoverageHoursQueryError';
  }
}

export type CoverageHoursDayOfWeek =
  | 'DAY_OF_WEEK_SUNDAY'
  | 'DAY_OF_WEEK_MONDAY'
  | 'DAY_OF_WEEK_TUESDAY'
  | 'DAY_OF_WEEK_WEDNESDAY'
  | 'DAY_OF_WEEK_THURSDAY'
  | 'DAY_OF_WEEK_FRIDAY'
  | 'DAY_OF_WEEK_SATURDAY';

export type CoverageHoursShiftBlock = {
  id: string;
  teamId: string | null;
  teamName: string | null;
  daysOfWeek: CoverageHoursDayOfWeek[];
  startMinute: number;
  endMinute: number;
  timezone: string | null;
};

export type CoverageHoursRouting = {
  routingId: string;
  category: string;
  issue: string | null;
  blocks: CoverageHoursShiftBlock[];
};

export type CoverageHoursPairing = Pick<
  CoverageHoursRouting,
  'category' | 'issue' | 'routingId'
>;

/** One block with its routing restamped onto it. */
export type CoverageHoursEntry = CoverageHoursShiftBlock & CoverageHoursPairing;

export type CoverageHoursPageInfo = {
  start: number;
  total: number;
  perPage: number;
};

type WireBlock = CoverageHoursShiftBlock & { __typename?: string };

type WireRouting = CoverageHoursPairing & {
  blocks: WireBlock[];
  __typename?: string;
};

type WirePageInfo = CoverageHoursPageInfo & { __typename?: string };

export type SearchCoverageHoursResponse = {
  searchCoverageHours: {
    coverageHours: WireRouting[];
    pageInfo: WirePageInfo | null;
  };
};

export type UseCoverageHoursArgs = {
  channelId: string;
  pageIndex: number;
  perPage?: number;
};

export type UseCoverageHoursResult = {
  routings: CoverageHoursRouting[];
  pageInfo: CoverageHoursPageInfo | null;
  totalRows: number;
  loading: boolean;
  error?: CoverageHoursQueryError;
  fetchCoverageHours: () => Promise<CoverageHoursRouting[]>;
  refreshCoverageHours: () => Promise<void>;
};

const NO_ROUTINGS: CoverageHoursRouting[] = [];

// The UUID scalar sends ids back base62-encoded, while the assignable-team
// options a teamId is matched against are canonical UUIDs.
function toTeamId(teamId: string | null | undefined): string | null {
  if (!teamId) {
    return null;
  }

  try {
    return convertToUUID(teamId);
  } catch {
    return teamId;
  }
}

function toCoverageHoursShiftBlock(block: WireBlock): CoverageHoursShiftBlock {
  return {
    id: block.id,
    teamId: toTeamId(block.teamId),
    teamName: block.teamName ?? null,
    daysOfWeek: block.daysOfWeek,
    startMinute: block.startMinute,
    endMinute: block.endMinute,
    timezone: block.timezone ?? null,
  };
}

function toCoverageHoursRouting(routing: WireRouting): CoverageHoursRouting {
  return {
    routingId: routing.routingId,
    category: routing.category,
    issue: routing.issue ?? null,
    blocks: routing.blocks.map(toCoverageHoursShiftBlock),
  };
}

export function toCoverageHoursEntries(
  routing: WireRouting
): CoverageHoursEntry[] {
  const mapped = toCoverageHoursRouting(routing);

  return mapped.blocks.map((block) => ({
    ...block,
    routingId: mapped.routingId,
    category: mapped.category,
    issue: mapped.issue,
  }));
}

export function toCoverageHoursPageInfo(
  pageInfo: WirePageInfo | null | undefined
): CoverageHoursPageInfo | null {
  if (!pageInfo) {
    return null;
  }

  return {
    start: pageInfo.start,
    total: pageInfo.total,
    perPage: pageInfo.perPage,
  };
}

export function useCoverageHours({
  channelId,
  pageIndex,
  perPage = COVERAGE_HOURS_PAGE_SIZE,
}: UseCoverageHoursArgs): UseCoverageHoursResult {
  const variables = useMemo(
    () => ({
      channelId,
      pagination: {
        start: pageIndex * perPage,
        perPage,
      },
    }),
    [channelId, pageIndex, perPage]
  );

  const [
    executeSearch,
    { data, previousData, loading, called, error: queryError },
  ] = useLazyQuery<SearchCoverageHoursResponse>(searchCoverageHours, {
    fetchPolicy: 'network-only',
  });

  const fetchCoverageHours = useCallback(async () => {
    const result = await executeSearch({ variables });

    return result.data
      ? result.data.searchCoverageHours.coverageHours.map(toCoverageHoursRouting)
      : [];
  }, [executeSearch, variables]);

  const refreshCoverageHours = useCallback(async () => {
    let refreshError: ApolloError | undefined;

    try {
      refreshError = (await executeSearch({ variables })).error;
    } catch (cause) {
      throw new CoverageHoursQueryError('Could not load coverage hours', {
        cause,
      });
    }

    if (refreshError) {
      throw new CoverageHoursQueryError('Could not load coverage hours', {
        cause: refreshError,
      });
    }
  }, [executeSearch, variables]);

  // previousData keeps the rows and the pagination bar mounted while the next
  // page is in flight.
  const result =
    data?.searchCoverageHours ?? previousData?.searchCoverageHours ?? null;

  const routings = useMemo(
    () => (result ? result.coverageHours.map(toCoverageHoursRouting) : NO_ROUTINGS),
    [result]
  );

  const pageInfo = useMemo(
    () => toCoverageHoursPageInfo(result?.pageInfo),
    [result]
  );

  const error = useMemo(() => {
    if (!queryError) {
      return undefined;
    }

    return new CoverageHoursQueryError('Could not load coverage hours', {
      cause: queryError,
    });
  }, [queryError]);

  return {
    routings,
    pageInfo,
    totalRows: pageInfo?.total ?? 0,
    loading: loading || !called,
    error,
    fetchCoverageHours,
    refreshCoverageHours,
  };
}
